import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServices, useCreateService, useUpdateService, useDeleteService, useRooms, useStaff } from '@hooks/useAdminResources';
import { useAuth } from '@hooks/useAuth';
import { useActiveCenter } from '@hooks/useActiveCenter';
import { useConfirm } from '@hooks/useConfirm';
import { adminCentersApi, adminServicesApi } from '@api/client';
import type { Center, Service } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { RowMenu } from '@components/admin/RowMenu';
import { formatMoney } from '../../lib/money';
import { ServiceModal } from './services/ServiceModal';


export function ServicesPage() {
  const { user } = useAuth();
  const { activeCenterId } = useActiveCenter();
  const confirm = useConfirm();
  // El catálogo es global, pero el modal sigue mostrando staff/salas del
  // centro activo del superadmin para configurar compatibilidades por contexto.
  // The catalogue is global, but the modal still scopes its staff/rooms
  // picker to the currently-selected centre to configure compatibilities.
  // `'all'` is irrelevant here — when the operator hasn't pinned a centre
  // we fall back to an empty string so the modal hides its centre-scoped
  // pickers gracefully.
  const centerId = activeCenterId === 'all' ? '' : activeCenterId;

  const { data: services = [], isLoading } = useServices();
  const { data: rooms = [] } = useRooms(centerId);
  const { data: center } = useQuery<Center>({
    queryKey: ['center', 'me'],
    queryFn: () => adminCentersApi.getMe(),
    enabled: !!user,
  });
  const createMutation = useCreateService();
  const updateMutation = useUpdateService();
  const deleteMutation = useDeleteService();

  const [q, setQ] = useState('');
  const [modalService, setModalService] = useState<Service | null | undefined>(undefined);

  // Post-0028: el cuello de botella físico de salas se modela con la pivot
  // `service_rooms` (opt-in). Solo se ofrece la UI si el centro activo tiene
  // `roomsAffectAvailability=true`.
  const showRoomsCompat = !!center?.roomsAffectAvailability;
  const { data: staffList = [] } = useStaff(centerId);

  const filtered = useMemo(() => {
    if (!q) return services;
    const s = q.toLowerCase();
    return services.filter((svc) => svc.name.toLowerCase().includes(s));
  }, [services, q]);

  const activeCount = services.filter((s) => s.active).length;

  async function handleSave(
    data: Record<string, unknown>,
    compatibleRoomIds: string[] | null,
    compatibleStaffIds: string[] | null,
  ) {
    let serviceId: string | null = null;
    if (modalService === null) {
      const created = await createMutation.mutateAsync(data) as { id?: string };
      serviceId = created.id ?? null;
    } else if (modalService) {
      await updateMutation.mutateAsync({ id: modalService.id, data });
      serviceId = modalService.id;
    }
    if (serviceId && compatibleRoomIds !== null) {
      await adminServicesApi.setRooms(serviceId, compatibleRoomIds);
    }
    if (serviceId && compatibleStaffIds !== null) {
      await adminServicesApi.setStaff(serviceId, compatibleStaffIds);
    }
    setModalService(undefined);
  }

  async function handleDelete(svc: Service) {
    const ok = await confirm({
      title: 'Eliminar servicio',
      message: `¿Eliminar el servicio "${svc.name}"? Esta acción no se puede deshacer.`,
      tone: 'danger',
      confirmLabel: 'Eliminar',
    });
    if (ok) {
      await deleteMutation.mutateAsync(svc.id);
    }
  }

  async function toggleActive(svc: Service) {
    await updateMutation.mutateAsync({ id: svc.id, data: { active: !svc.active } });
  }

  return (
    <div>
      <PageHead
        title="Servicios"
        sub="Gestiona el catálogo, precios y duración. Los cambios se reflejan en el widget."
        meta={<span className="mono">{activeCount} activos · {services.length} total</span>}
        actions={
          <button className="btn" data-variant="primary" onClick={() => { setModalService(null); }}>
            <Icons.Plus size={14} /> Nuevo servicio
          </button>
        }
      />

      <div className="toolbar">
        <div className="search">
          <Icons.Search size={14} className="ico" />
          <input
            className="input"
            placeholder="Buscar servicio…"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
          />
        </div>
        <div className="toolbar-spacer" />
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>
          {filtered.length} resultados
        </span>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Servicio</th>
              <th className="cell-right">Duración</th>
              <th className="cell-right">Precio</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Cargando…</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Sin servicios</td></tr>
            )}
            {filtered.map((svc) => (
              <tr key={svc.id} data-clickable="true" onClick={() => { setModalService(svc); }}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {svc.color && (
                      <span
                        aria-hidden
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: svc.color,
                          flexShrink: 0,
                          border: '1px solid var(--border)',
                        }}
                      />
                    )}
                    <div>
                      <div className="cell-bold">{svc.name}</div>
                      {svc.category && (
                        <div style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 2 }}>
                          {svc.category}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="cell-right mono">{svc.durationMinutes} min</td>
                <td className="cell-right mono">
                  {formatMoney(svc.price, center?.currency ?? 'EUR')}
                </td>
                <td>
                  <div
                    className="switch"
                    data-on={svc.active}
                    onClick={(e) => { e.stopPropagation(); void toggleActive(svc); }}
                  />
                </td>
                <td className="cell-narrow">
                  <div className="row-actions">
                    <button
                      className="icon-btn"
                      data-variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setModalService(svc); }}
                    >
                      <Icons.Edit size={13} />
                    </button>
                    <RowMenu items={[
                      { icon: Icons.Edit, label: 'Editar', onClick: () => { setModalService(svc); } },
                      { icon: Icons.Tag, label: svc.active ? 'Desactivar' : 'Activar', onClick: () => void toggleActive(svc) },
                      null,
                      { icon: Icons.Trash, label: 'Eliminar', tone: 'danger', onClick: () => { void handleDelete(svc); } },
                    ]} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalService !== undefined && (
        <ServiceModal
          service={modalService}
          rooms={rooms.filter((r) => r.isActive)}
          staff={staffList.filter((s) => s.active)}
          showRoomsCompat={showRoomsCompat}
          onClose={() => { setModalService(undefined); }}
          onSave={handleSave}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

    </div>
  );
}
